import { BasePageDto } from "@/types/dto/base-page-dto";
import { CreateOrderDto } from "@/types/dto/create-order-dto";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { CreateOrderPayload } from "@/types/payload/create-order-payload";
import {
  OrderPagePartnerPayload,
  OrderPagePayload,
} from "@/types/payload/order-page-payload";
import { PaymentPayload } from "@/types/payload/payment-payload";
import { AxiosInstance } from "axios";
import { BaseResponse } from "../types/dto/base-response"; // 공통 응답 타입
import { HistoryDto } from "../types/dto/history-dto";
import { baseApi } from "./base-api"; // 기본 API 설정

// 엔드포인트 상수
enum OrderServiceEndpoint {
  Base = "/invoice",
  TestApi = "/order/test", // 예시 엔드포인트
  Create = "/invoice",
  GetById = "/invoice",
  GetPage = "/invoices",
  Payment = "/payment",
  Cancel = "/cancel",
  PatchNotes = "/patch-notes",
  Validate = "/validate",
  List = "/invoice-list",
}

class OrderService {
  private static instance: OrderService;
  private _api: AxiosInstance;

  // private 생성자: 외부에서 클래스 생성 방지
  private constructor(api: AxiosInstance) {
    this._api = api;
  }

  // 싱글톤 패턴을 통해 인스턴스 제공
  static getInstance(apiClient = baseApi): OrderService {
    if (!OrderService.instance) {
      OrderService.instance = new OrderService(apiClient);
    }
    return OrderService.instance;
  }

  async create(
    payload: CreateOrderPayload
  ): Promise<BaseResponse<CreateOrderDto, CreateOrderPayload>> {
    try {
      const response = await this._api.post<
        BaseResponse<CreateOrderDto, CreateOrderPayload>
      >(OrderServiceEndpoint.Create, payload);
      return response.data;
    } catch (error) {
      console.error("Error in createOrder:", error);
      throw error;
    }
  }

  async getById(id: number): Promise<BaseResponse<OrderDetailDto, void>> {
    try {
      const response = await this._api.get<BaseResponse<OrderDetailDto, void>>(
        `${OrderServiceEndpoint.GetById}/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Error in getById:", error);
      throw error;
    }
  }

  async getPage(
    payload: OrderPagePayload
  ): Promise<BaseResponse<BasePageDto<OrderDetailDto>, OrderPagePayload>> {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", payload.page.toString());
      queryParams.append("size", payload.size.toString());
      if (payload.fromDate) {
        queryParams.append("fromDate", payload.fromDate.toISOString());
      }
      if (payload.toDate) {
        queryParams.append("toDate", payload.toDate.toISOString());
      }
      if (payload.orderType) {
        queryParams.append("orderType", payload.orderType.toString());
      }
      if (payload.partnerName && payload.partnerName !== "") {
        queryParams.append("partnerName", payload.partnerName);
      }

      if (payload.keyword && payload.keyword !== "") {
        queryParams.append("keyword", payload.keyword);
      }
      // 운송장 번호가 필요한 주문내역 조회
      if (payload.needTrackingNumber) {
        queryParams.append(
          "needTrackingNumber",
          payload.needTrackingNumber.toString()
        );
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<OrderDetailDto>, OrderPagePayload>
      >(`${OrderServiceEndpoint.GetPage}?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      console.error("Error in getPage:", error);
      throw error;
    }
  }

  async getPagePartner(
    id: number,
    payload: OrderPagePartnerPayload
  ): Promise<
    BaseResponse<BasePageDto<OrderDetailDto>, OrderPagePartnerPayload>
  > {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", payload.page.toString());
      queryParams.append("size", payload.size.toString());
      if (payload.fromDate) {
        queryParams.append("fromDate", payload.fromDate.toISOString());
      }
      if (payload.toDate) {
        queryParams.append("toDate", payload.toDate.toISOString());
      }
      if (payload.orderType) {
        queryParams.append("orderType", payload.orderType.toString());
      }
      if (payload.constructionName && payload.constructionName !== "") {
        queryParams.append("constructionName", payload.constructionName);
      }

      const response = await this._api.get<
        BaseResponse<BasePageDto<OrderDetailDto>, OrderPagePartnerPayload>
      >(`${OrderServiceEndpoint.GetPage}/${id}?${queryParams.toString()}`);

      return response.data;
    } catch (error) {
      console.error("Error in getPage:", error);
      throw error;
    }
  }

  async payment(
    id: number,
    payload: PaymentPayload
  ): Promise<BaseResponse<void, PaymentPayload>> {
    try {
      const response = await this._api.post<BaseResponse<void, PaymentPayload>>(
        OrderServiceEndpoint.Base + "/" + id + OrderServiceEndpoint.Payment,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error in createPayment:", error);
      throw error;
    }
  }

  async cancel(id: number, data?: any): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.patch<BaseResponse<void, void>>(
        OrderServiceEndpoint.Base + "/" + id + OrderServiceEndpoint.Cancel,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error in cancel:", error);
      throw error;
    }
  }

  async getPatchNotes(id: number): Promise<BaseResponse<HistoryDto[], void>> {
    try {
      const response = await this._api.get<BaseResponse<HistoryDto[], void>>(
        `${OrderServiceEndpoint.Base}/${id}${OrderServiceEndpoint.PatchNotes}`
      );
      return response.data;
    } catch (error) {
      console.error("Error in patchNotes:", error);
      throw error;
    }
  }

  async validate(id: number): Promise<BaseResponse<void, void>> {
    try {
      const response = await this._api.get<BaseResponse<void, void>>(
        OrderServiceEndpoint.Base + `/${id}` + OrderServiceEndpoint.Validate
      );
      return response.data;
    } catch (error) {
      console.error("Error in validate:", error);
      throw error;
    }
  }

  async getInvoiceList({
    paymentType,
    paymentStatus,
  }: {
    paymentType: string;
    paymentStatus: string;
  }): Promise<BaseResponse<OrderDetailDto[], void>> {
    try {
      const response = await this._api.get<
        BaseResponse<OrderDetailDto[], void>
      >(
        OrderServiceEndpoint.List +
          "?type=" +
          paymentType +
          "&status=" +
          paymentStatus
      );
      return response.data;
    } catch (error) {
      console.error("Error in order Service get Invoices:", error);
      throw error;
    }
  }

  async updateShippingMemo(id: number, shippingMemo: string): Promise<void> {
    try {
      await this._api.patch(`/invoice/${id}`, { shippingMemo });
    } catch (error) {
      console.error("Error in updateShippingMemo:", error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스 생성 및 export
export const orderService = OrderService.getInstance(baseApi);
